import { useEffect, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "@inertiajs/react";
import { cn } from "@/lib/cn";

/*
 * ListTemplate — page chrome for "index of records" pages: breadcrumbs,
 * title/description, a filters slot, and a fill-height scroll body with an
 * optional sticky footer (the pager). Sections fill slots; they don't author
 * the layout.
 */

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface ListTemplateProps {
  title: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  primaryAction?: ReactNode;
  meta?: ReactNode;
  toolbar?: ReactNode;
  filters?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

export function ListTemplate({
  title,
  description,
  breadcrumbs,
  primaryAction,
  meta,
  toolbar,
  filters,
  footer,
  children,
}: ListTemplateProps) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · stabiliti`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border-subtle bg-canvas px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-2 flex items-center gap-1 text-xs text-fg-subtle">
            {breadcrumbs.map((bc, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="size-3" />}
                {bc.to ? (
                  <Link href={bc.to} className="hover:text-fg">{bc.label}</Link>
                ) : (
                  <span className={cn(i === breadcrumbs.length - 1 && "text-fg")}>{bc.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-fg">{title}</h1>
            {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            {meta}
            {primaryAction}
          </div>
        </div>

        {(toolbar || filters) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {toolbar}
            {filters && (
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto">{filters}</div>
            )}
          </div>
        )}
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-auto px-4 py-3 sm:px-6 sm:py-4">{children}</div>
        {footer && (
          <div className="border-t border-border-subtle bg-canvas px-4 py-3 sm:px-6">{footer}</div>
        )}
      </div>
    </div>
  );
}

/*
 * Read & write a single string filter from the URL query string. Sections call
 * this per-filter; the template owns the URL contract. Updates use
 * history.replaceState (no full reload); the caller triggers the Inertia fetch.
 */
export function useUrlFilter(key: string, defaultValue = "") {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return defaultValue;
    return new URLSearchParams(window.location.search).get(key) ?? defaultValue;
  });

  const setValueAndUrl = (next: string) => {
    setValue(next);
    const params = new URLSearchParams(window.location.search);
    if (!next || next === defaultValue) params.delete(key);
    else params.set(key, next);
    if (key !== "page") params.delete("page");
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", newUrl);
  };

  useEffect(() => {
    const onPop = () => {
      const v = new URLSearchParams(window.location.search).get(key) ?? defaultValue;
      setValue(v);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [key, defaultValue]);

  return [value, setValueAndUrl] as const;
}
